import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from '../i18n';
import type { TranslationKey } from '../i18n/types';
import { api } from '../lib/api';
import { apiErrorMessage } from '../lib/errors';
import { formatDateTime } from '../lib/utils';
import { countdownTarget, PHASE_KEY, RAIL_KEY, phaseTone, railTone } from '../lib/contest';
import type { Contest, Rail } from '../types';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Alert } from '../components/ui/alert';
import { Button } from '../components/ui/button';
import { FullPageLoader } from '../components/ui/spinner';
import { Countdown } from '../components/controls/Countdown';

type RailFilter = Rail | 'all';

interface ContestListPayload {
  contests: Contest[];
  serverTime: string;
}

/**
 * Contest discovery.
 *
 * Only publicly visible contests are returned by the API (drafts and cancelled
 * contests are filtered server-side), so this page cannot leak an unpublished
 * contest even if it asked for one.
 */
export default function ContestsPage() {
  const { t, locale } = useTranslation();
  const [contests, setContests] = useState<Contest[]>([]);
  const [serverTime, setServerTime] = useState('');
  const [fetchedAt, setFetchedAt] = useState(Date.now());
  const [rail, setRail] = useState<RailFilter>('all');
  const [enabledRails, setEnabledRails] = useState<Rail[]>(['crypto']);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Which rails this deployment offers is the SERVER's answer, not a build-time
    // constant: the client must not be able to reveal a rail the platform has
    // switched off, and it should not have to be rebuilt when one is enabled.
    let cancelled = false;
    void (async () => {
      try {
        const payload = await api.get<{ enabledRails: Rail[] }>('/contests/providers');
        if (!cancelled && Array.isArray(payload.enabledRails) && payload.enabledRails.length > 0) {
          setEnabledRails(payload.enabledRails);
        }
      } catch {
        // Fall back to the crypto-only default; the list request is the authority.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const query = rail === 'all' ? '' : `?rail=${rail}`;
      const data = await api.get<ContestListPayload>(`/contests${query}`);
      setContests(data.contests);
      setServerTime(data.serverTime);
      setFetchedAt(Date.now());
    } catch (error_) {
      setError(apiErrorMessage(error_, t));
    } finally {
      setLoading(false);
    }
  }, [rail, t]);

  useEffect(() => {
    void load();
  }, [load]);

  if (loading) {
    return <FullPageLoader label={t('common.loading')} />;
  }

  return (
    <div className="mx-auto max-w-6xl px-4 py-10">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-fg">
            {t('contest.list.title')}
          </h1>
          <p className="mt-1 text-sm text-muted">{t('contest.list.subtitle')}</p>
        </div>

        <div
          className="flex items-center gap-1 rounded-lg border border-border bg-surface p-1"
          role="group"
          aria-label={t('common.rail')}
        >
          {/* The filter only offers rails the server says are live, and only shows
              the "all" option when there is more than one to choose between — so a
              single-rail deployment reads as a plain heading, not a dead control. */}
          {(() => {
            // Explicitly typed so each key stays a literal translation key.
            const options: Array<{ value: RailFilter; key: TranslationKey }> =
              enabledRails.length > 1
                ? [
                    { value: 'all', key: 'rail.all' },
                    ...enabledRails.map((entry) => ({ value: entry, key: RAIL_KEY[entry] })),
                  ]
                : enabledRails.map((entry) => ({ value: entry, key: RAIL_KEY[entry] }));

            return options.map(({ value, key }) => (
              <button
                key={value}
                type="button"
                onClick={() => setRail(value)}
                aria-pressed={rail === value}
                className={
                  rail === value
                    ? 'rounded-md bg-brand-soft px-3 py-1.5 text-sm font-medium text-brand-soft-fg'
                    : 'rounded-md px-3 py-1.5 text-sm font-medium text-muted hover:text-fg'
                }
              >
                {t(key)}
              </button>
            ));
          })()}
        </div>
      </header>

      {error && (
        <div className="mt-6">
          <Alert tone="error">{error}</Alert>
        </div>
      )}

      {!error && contests.length === 0 && (
        <Card className="mt-8">
          <CardContent className="py-12 text-center text-sm text-muted">
            {t('contest.list.empty')}
          </CardContent>
        </Card>
      )}

      <ul className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {contests.map((contest) => {
          const countdown = countdownTarget(contest);
          return (
            <li key={contest.id}>
              <Card className="flex h-full flex-col">
                <CardContent className="flex flex-1 flex-col gap-3 pt-5">
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge tone={railTone(contest.rail)}>{t(RAIL_KEY[contest.rail])}</Badge>
                    <Badge tone={phaseTone(contest.phase)}>{t(PHASE_KEY[contest.phase])}</Badge>
                  </div>

                  <h2 className="text-base font-semibold text-fg">
                    <Link to={`/contests/${contest.id}`} className="hover:underline">
                      {contest.title}
                    </Link>
                  </h2>

                  {contest.summary && <p className="text-sm text-muted">{contest.summary}</p>}

                  <dl className="grid grid-cols-2 gap-x-3 gap-y-2 text-sm">
                    <div>
                      <dt className="text-xs uppercase tracking-wide text-muted">
                        {t('contest.card.entry')}
                      </dt>
                      <dd className="font-medium text-fg">
                        {contest.entryFee === '0'
                          ? t('contest.card.free')
                          : `${contest.entryFee} ${contest.asset}`}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-xs uppercase tracking-wide text-muted">
                        {t('contest.card.prizePool')}
                      </dt>
                      <dd className="font-medium text-fg">
                        {contest.prizePool} {contest.asset}
                      </dd>
                    </div>
                    <div className="col-span-2">
                      <dt className="text-xs uppercase tracking-wide text-muted">
                        {t('common.status')}
                      </dt>
                      <dd className="text-fg">
                        {t('contest.card.participants', { count: contest.participantCount })}
                      </dd>
                    </div>
                  </dl>

                  <div className="mt-auto flex flex-col gap-3 pt-2">
                    {countdown && serverTime ? (
                      <Countdown
                        label={t(countdown.labelKey)}
                        targetMs={countdown.at}
                        serverTime={serverTime}
                        fetchedAtMs={fetchedAt}
                        onExpire={() => void load()}
                      />
                    ) : (
                      <p className="text-xs text-muted">
                        {t('contest.card.startsAt', {
                          date: formatDateTime(contest.startsAt, locale),
                        })}
                      </p>
                    )}

                    <Link to={`/contests/${contest.id}`}>
                      <Button variant="outline" fullWidth>
                        {t('contest.list.view')}
                      </Button>
                    </Link>
                  </div>
                </CardContent>
              </Card>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
